// src/encounter/lib/malaffi.ts
// Malaffi HIE single-sign-on — the pure half (gate + URL build). The popup lifecycle lives in
// services/use-malaffi-sso.ts (the WebTabManager port).
//
// FLUTTER SOURCE (this is a straight port; Malaffi is NOT in the divergence register):
//   · ui_data.dart:127-137 `patientEncounterTabs` / :139-149 `newAppointmentTabs` — the tab list is
//     [Encounter, Chart] plus a THIRD "Malaffi" tab, present only when
//     `userData.resourceBasedIntegration.integrationType.toLowerCase() == 'malaffi'`.
//   · encounter_screen.dart:1075-1077 `malaffiSSOView()` returns an EMPTY Container — the tab has no
//     content. It is an ACTION tab: encounter_screen.dart:182-188 intercepts activation of index 2
//     when its title is "Malaffi", calls `_openUrl()`, then `animateTo(0)` — snapping straight back
//     to Encounter. Port the ACTION, not a content pane.
//   · encounter_screen.dart:212-217 `_openUrl()` —
//       "${getMalaffiSSOBase(currentENV)}?q1=${appointment.patient.mrn}&q2=$providerid"
//     where `providerid` = `providerId()` (utils.dart:1232-1249) with the DEFAULT
//     `forceProviderIdOnly: false` → the logged-in user's `id`, NOT `practitionerId`.

/** The org's HIE integration is Malaffi. Case-insensitive, matching Flutter's `.toLowerCase()`
 *  comparison; absent/blank → false (fails closed: no tab). */
export function isMalaffiIntegration(integrationType?: string | null): boolean {
  return (integrationType ?? '').trim().toLowerCase() === 'malaffi';
}

export interface MalaffiSsoParams {
  /** SSO initiator base (env.malaffiSsoUrl) — per-environment; empty → no SSO. */
  baseUrl?: string | null;
  /** Patient MRN → `q1`. */
  mrn?: string | null;
  /** Logged-in user id → `q2` (Flutter `providerId()` = LoginUserData.id). */
  providerId?: string | null;
}

/**
 * Build the Malaffi SSO initiator URL, or `null` when any part is missing.
 *
 * Null means "can't hand off yet", NOT "hide the tab" — tab visibility keys on the org flag alone
 * (see `isMalaffiIntegration` / useMalaffiSso). Returning null rather than a half-built URL is what
 * stops an SSO session opening pointed at no patient. Both params are percent-encoded —
 * Flutter interpolates them raw, which is safe only because MRNs/GUIDs are alphanumeric; encoding
 * is identical for those values and correct for anything else.
 */
export function buildMalaffiSsoUrl({ baseUrl, mrn, providerId }: MalaffiSsoParams): string | null {
  const base = baseUrl?.trim();
  const q1 = mrn?.trim();
  const q2 = providerId?.trim();
  if (!base || !q1 || !q2) return null;
  return `${base}?q1=${encodeURIComponent(q1)}&q2=${encodeURIComponent(q2)}`;
}

/** Popup geometry, verbatim from Flutter `WebTabManager.openPopup` (web_tab_manager.dart:53-61). */
export const MALAFFI_POPUP_NAME = 'popup';
export const MALAFFI_POPUP_FEATURES = 'width=800,height=600,resizable,scrollbars=no,status=no';
